import React, { useEffect, useMemo, useState } from "react";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import PersonIcon from "@mui/icons-material/Person";
import AxiosInstance from "../../AxiosInstance";
import moment from "moment";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import { useLocation, useNavigate } from "react-router-dom";
import { handleAuth } from "../../../components/Login/Auth";
import "../style.css";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import BlueButton from "../../../components/Button/BlueButton";
import WhiteButton from "../../../components/Button/WhiteButton";
import { LoaderComponent, EditIcon } from "../../../components/Icon/Index";
import {
  CardContent,
  Grid,
  Typography,
  Paper,
  Box,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  useTheme,
} from "@mui/material";
import InputText from "../../../components/InputFields/InputText";
import Contract from "../../../assets/Blue-sidebar-icon/Contract.svg";
import CustomSwal from "../../../components/Swal/CustomSwal";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import showToast from "../../../components/Toast/Toster";
import {
  JobberStatusBadge,
  getVisitAppointmentStatusTone,
} from "../../../components/MuiTable/index.jsx";

const ArrowBackIcon = ArrowBackOutlinedIcon;

/** Align with staff appointment-confirm + portal tokens: Navy Deep + Amber */
const themeColors = {
  orange: "#D97706",
  blue: "#0F2B46",
  lightOrange: "rgba(212, 148, 58, 0.12)",
  lightBlue: "rgba(15, 43, 70, 0.06)",
};

/** Edit tile — same as staff Worker Time Entry / appointment-confirm */
const ML_EDIT_TILE_BG = "#DCFCE7";
const ML_EDIT_TILE_BORDER = "rgba(4, 120, 87, 0.18)";
const ML_EDIT_ICON = "#047857";

/** Customer confirm appointment — layout/spacing/borders; palette-aware for dark mode */
const buildConfirmLayout = (theme) => {
  const { palette } = theme;
  const isDark = palette.mode === "dark";
  return {
    page: { pb: { xs: 2, md: 3 } },
    contentWrap: {
      width: "100%",
      maxWidth: "100%",
      margin: 0,
      padding: { xs: "0.75rem", sm: "1rem", md: "1.25rem" },
      boxSizing: "border-box",
    },
    mainCard: {
      border: `1px solid ${palette.divider}`,
      borderRadius: "12px",
      overflow: "hidden",
      backgroundColor: palette.background.paper,
      boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.35)" : "var(--shadow-sm)",
    },
    headerBar: {
      backgroundColor: "transparent",
      color: isDark ? palette.text.primary : themeColors.blue,
      px: { xs: 2, sm: 2.5 },
      py: { xs: 1.5, sm: 1.75 },
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 2,
      flexWrap: "wrap",
    },
    contentBody: {
      padding: { xs: "20px", sm: "28px" },
    },
    infoCard: {
      background: isDark ? palette.action.hover : "#F9FAFB",
      border: `1px solid ${palette.divider}`,
      borderRadius: "12px",
      padding: { xs: "14px", sm: "18px" },
      height: "100%",
    },
    infoLabel: {
      color: isDark ? palette.text.secondary : "#9CA3AF",
      fontWeight: 500,
      fontSize: { xs: "11px", sm: "12px" },
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      marginBottom: { xs: "8px", sm: "10px" },
    },
    materialsPanel: {
      border: `1px solid ${palette.divider}`,
      borderRadius: "12px",
      backgroundColor: palette.background.paper,
      p: { xs: 2, sm: 2.5 },
    },
    dateChangeWrap: {
      border: `1px solid ${palette.divider}`,
      borderRadius: "12px",
      backgroundColor: palette.background.paper,
      overflow: "hidden",
    },
    dateChangeHeader: {
      color: isDark ? palette.text.primary : "#0F2B46",
      px: { xs: 2, sm: 2.5 },
      py: { xs: 1.25, sm: 1.5 },
    },
  };
};

// Helper function to format time from ISO string or various formats
const formatTimeFromString = (timeString) => {
  if (!timeString) return null;
  
  const timeStr = String(timeString).trim();
  if (!timeStr) return null;
  
  // Try parsing as ISO string first (from TimePicker's toISOString())
  // ISO strings look like "2024-01-01T14:30:00.000Z" or "2024-01-01T14:30:00Z"
  let timeMoment = moment(timeStr);
  
  // If ISO parsing succeeds, extract just the time
  if (timeMoment.isValid()) {
    // Check if it's an ISO string (contains 'T' and possibly 'Z')
    if (timeStr.includes('T') || timeStr.includes('Z')) {
      // Extract time portion and format it
      return timeMoment.format("hh:mm A");
    }
    // If it's already a valid moment, format it
    return timeMoment.format("hh:mm A");
  }
  
  // If ISO parsing fails, try common time formats
  // Try HH:mm format
  timeMoment = moment(timeStr, "HH:mm", true);
  if (timeMoment.isValid()) {
    return timeMoment.format("hh:mm A");
  }
  
  // Try hh:mm A format (already in 12-hour format)
  timeMoment = moment(timeStr, ["hh:mm A", "h:mm A", "HH:mm:ss", "HH:mm"], true);
  if (timeMoment.isValid()) {
    return timeMoment.format("hh:mm A");
  }
  
  return null;
};

// Helper function to combine StartDate and StartTime for Recurring contracts
const combineDateAndTime = (startDate, startTime) => {
  if (!startDate) return null;
  if (!startTime) return startDate; // If no StartTime, return StartDate as is
  
  try {
    // Parse StartDate - get date components (year, month, day)
    // StartDate from backend is in UTC, but we need to extract just the date part
    const dateMoment = moment.utc(startDate);
    if (!dateMoment.isValid()) {
      // Fallback: try parsing as local date
      const localMoment = moment(startDate).startOf('day');
      if (!localMoment.isValid()) return startDate;
      dateMoment.year(localMoment.year()).month(localMoment.month()).date(localMoment.date()).hour(0).minute(0).second(0).millisecond(0);
    } else {
      // Reset to start of day in UTC
      dateMoment.hour(0).minute(0).second(0).millisecond(0);
    }
    
    // Parse StartTime - handle both 12h (09:30 PM) and 24h (21:30) formats
    // StartTime is stored as entered by user (e.g., "09:30 PM")
    const timeStr = String(startTime).trim();
    let hours, minutes;
    
    // Check if it's 12-hour format (contains AM/PM)
    if (timeStr.match(/[AaPp][Mm]/)) {
      // 12-hour format (e.g., "09:30 PM", "10:00 AM")
      const timeMoment = moment(timeStr, ['h:mm A', 'hh:mm A', 'H:mm A', 'HH:mm A', 'h:mm:ss A', 'hh:mm:ss A'], true);
      if (timeMoment.isValid()) {
        hours = timeMoment.hour();
        minutes = timeMoment.minute();
      } else {
        console.warn("Failed to parse 12h StartTime:", startTime);
        return startDate;
      }
    } else {
      // 24-hour format (e.g., "21:30" or "21:30:00")
      const timeParts = timeStr.split(':');
      if (timeParts.length >= 2) {
        hours = parseInt(timeParts[0], 10);
        minutes = parseInt(timeParts[1], 10);
        if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours >= 24 || minutes < 0 || minutes >= 60) {
          console.warn("Invalid 24h time values:", startTime);
          return startDate;
        }
      } else {
        console.warn("Failed to parse 24h StartTime:", startTime);
        return startDate;
      }
    }
    
    // Create date+time string in format "YYYY-MM-DD HH:mm:ss"
    // This represents the date and time as entered by user
    const year = dateMoment.year();
    const month = String(dateMoment.month() + 1).padStart(2, '0');
    const day = String(dateMoment.date()).padStart(2, '0');
    const hoursStr = String(hours).padStart(2, '0');
    const minutesStr = String(minutes).padStart(2, '0');
    
    // Create ISO string: "YYYY-MM-DDTHH:mm:ss" 
    // This will be parsed as local time by formatDateWithUserTimezone
    const dateTimeString = `${year}-${month}-${day} ${hoursStr}:${minutesStr}:00`;
    
    // Return the string - formatDateWithUserTimezone will parse it correctly
    return dateTimeString;
  } catch (error) {
    console.error("Error combining date and time:", error, "StartDate:", startDate, "StartTime:", startTime);
    return startDate; // Return original on error
  }
};

function Quotes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loader, setLoader] = useState(true);
  const [quotesData, setQuotesData] = useState([]);
  const [tokenDecode, setTokenDecode] = useState({});
  const [isConfirmed, setIsConfirmed] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showConfirmSwal, setShowConfirmSwal] = useState(false);
  const [showSuccessSwal, setShowSuccessSwal] = useState(false);
  const [showErrorSwal, setShowErrorSwal] = useState(false);
  const [DateDecode, setDateDecode] = useState("MM/DD/YYYY");
  const [showChangeDateDialog, setShowChangeDateDialog] = useState(false);
  const [newDate, setNewDate] = useState(null);
  const [dateChangeReason, setDateChangeReason] = useState("");
  const [dateChangeLoading, setDateChangeLoading] = useState(false);
  const [showDateChangeErrorSwal, setShowDateChangeErrorSwal] = useState(false);
  const [contractVisits, setContractVisits] = useState([]);
  
  // Use timezone-aware date formatter hook
  const formatDateWithUserTimezone = useUserTimezoneFormatter();
  const theme = useTheme();
  const confirmLayout = useMemo(() => buildConfirmLayout(theme), [theme]);
  
  const fetchData = async () => {
    try {
      const res = await handleAuth(navigate, location);
      setTokenDecode(res.data);
      setDateDecode(res?.themes?.Format || "MM/DD/YYYY");
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const CompanyId = tokenDecode?.CompanyId;
  useEffect(() => {
    handleAuth(navigate, location);

    if (CompanyId && location?.state?.id) {
      setLoader(true);
      AxiosInstance.get(
        `/v1/visit/customervisits/${location?.state?.id}/${location?.state?.contractId}`
      )
        .then((response) => {
          if (response?.data?.statusCode === 200) {
            setQuotesData(response?.data?.data);
            const visitData = response?.data?.data[0];
            
            // Debug console logs
         
            
            // Check if visit is confirmed by customer, worker, or company
            const isCustomerConfirmed = visitData?.IsConfirm === true;
            const isWorkerConfirmed = visitData?.IsConfirmByWorker === true || 
              (visitData?.ConfirmWorker && visitData.ConfirmWorker.length > 0);
            const isStatusConfirmed = visitData?.Status === "Confirmed";
            const isStatusCompleted = visitData?.Status === "Completed";
            const isVisitCompleted = visitData?.IsComplete === true || isStatusCompleted;
            
            // Visit is confirmed if any party confirmed it
            const visitIsConfirmed = isCustomerConfirmed || isWorkerConfirmed || isStatusConfirmed;
            
            if (visitIsConfirmed !== undefined) {
              setIsConfirmed(visitIsConfirmed);
            } else {
              const savedStatus = localStorage?.getItem("isConfirmed");
              setIsConfirmed(savedStatus === "true");
            }
            
            // Set completion status
            setIsComplete(isVisitCompleted);
          } else {
            console.error("Error fetching data");
          }
        })
        .catch((error) => {
          console.error("API error:", error);
        })
        .finally(() => {
          setLoader(false);
        });

      // Fetch date change requests
      AxiosInstance.get(
        `/v1/visit/date-change-requests/${location?.state?.id}/${location?.state?.contractId}`
      )
        .then((response) => {
          if (response?.data?.statusCode === 200) {
            setDateChangeRequests(response?.data?.data || []);
            const pendingRequests = (response?.data?.data || []).filter(req => req.Status === "Pending");
            if (pendingRequests.length > 0) {
              setPendingRequest(pendingRequests[0]);
            } else {
              setPendingRequest(null);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching date change requests:", error);
        });
    } else {
      console.error("CompanyId or Visit ID is not valid.");
      setLoader(false);
    }
  }, [navigate, location, CompanyId]);

  const handleConfirmAppointment = () => {
    setShowConfirmSwal(true);
  };

  const handleConfirmAppointmentConfirm = async (reason) => {
    const { LocationId, ContractId, CustomerId, CompanyId, VisitId } =
      quotesData[0] || {};

    const dataToSend = {
      LocationId,
      ContractId,
      CustomerId,
      CompanyId,
      VisitId,
      AddedAt: new Date(),
    };

    try {
      await AxiosInstance.put(
        `/v1/visit/confirm-customer/${location?.state?.id}/${location?.state?.contractId}`,
        dataToSend
      );
      setIsConfirmed(true);
      localStorage?.setItem("isConfirmed", "true");
      setShowConfirmSwal(false);
      setShowSuccessSwal(true);
    } catch (error) {
      console.error("Error confirming appointment:", error);
      setShowConfirmSwal(false);
      setShowErrorSwal(true);
    }
  };

  const handleConfirmAppointmentCancel = () => {
    setShowConfirmSwal(false);
  };

  const [originalDate, setOriginalDate] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [dateChangeRequests, setDateChangeRequests] = useState([]);

  const handleOpenChangeDateDialog = async () => {
    // Check if visit is confirmed or completed - prevent date changes
    if (isConfirmed || isComplete) {
      showToast.warning("Cannot change visit date. The visit has been confirmed or completed. Date changes are not allowed after confirmation or completion.");
      return;
    }

    // Check for pending request first
    try {
      const response = await AxiosInstance.get(
        `/v1/visit/date-change-requests/${location?.state?.id}/${location?.state?.contractId}`
      );
      
      if (response?.data?.statusCode === 200 && response?.data?.data) {
        const pendingRequests = response.data.data.filter(req => req.Status === "Pending");
        if (pendingRequests.length > 0) {
          showToast.warning("A visit date change request is already pending for this visit.");
          setPendingRequest(pendingRequests[0]);
          return;
        }
      }
    } catch (error) {
      console.error("Error checking pending requests:", error);
    }

    if (quotesData[0]?.StartDate) {
      const currentDate = dayjs(quotesData[0].StartDate);
      setNewDate(currentDate);
      setOriginalDate(currentDate);
    }
    setPendingRequest(null);
    setShowChangeDateDialog(true);
  };

  const handleCloseChangeDateDialog = () => {
    setShowChangeDateDialog(false);
    setNewDate(null);
    setOriginalDate(null);
    setDateChangeReason("");
  };

  // Function to check if a date already has a visit booked
  const shouldDisableDate = (date) => {
    if (!contractVisits || contractVisits.length === 0) {
      return false;
    }
    
    // Don't disable the current visit's date
    const currentVisitDate = quotesData[0]?.StartDate 
      ? dayjs(quotesData[0].StartDate).format("YYYY-MM-DD")
      : null;
    const checkDate = dayjs(date).format("YYYY-MM-DD");
    
    if (currentVisitDate === checkDate) {
      return false; // Allow selecting the current visit's date
    }
    
    // Check if any visit exists on this date
    return contractVisits.some(visit => {
      if (!visit.StartDate) return false;
      const visitDateStr = dayjs(visit.StartDate).format("YYYY-MM-DD");
      return visitDateStr === checkDate;
    });
  };

  const handleChangeDateConfirm = async () => {
    if (!newDate || !quotesData[0]) {
      return;
    }

    if (!dateChangeReason || !dateChangeReason.trim()) {
      return;
    }

    // Only proceed if date actually changed
    if (originalDate && newDate.isSame(originalDate, "day")) {
      setShowChangeDateDialog(false);
      setNewDate(null);
      setOriginalDate(null);
      setDateChangeReason("");
      return;
    }

    setDateChangeLoading(true);
    try {
      const formattedDate = moment(newDate.toDate()).format("YYYY-MM-DD");
      
      const response = await AxiosInstance.post(
        `/v1/visit/date-change-request/${location?.state?.id}/${location?.state?.contractId}`,
        {
          RequestedDate: formattedDate,
          Reason: dateChangeReason.trim(),
        }
      );

      if (response?.data?.statusCode === 200) {
        setShowChangeDateDialog(false);
        setNewDate(null);
        setOriginalDate(null);
        setDateChangeReason("");
        
        // Show success toast
        showToast.success("Date change request submitted successfully. Request is pending admin approval.");
        
        // Show email notification toasts for all emails (sent and failed)
      
        if (response?.data?.emailResults && Array.isArray(response.data.emailResults) && response.data.emailResults.length > 0) {
          response.data.emailResults.forEach((emailInfo, index) => {
            if (emailInfo && emailInfo.email && emailInfo.role) {
              setTimeout(() => {
                if (emailInfo.status === "sent") {
                  showToast.success(`Email sent to ${emailInfo.role}: ${emailInfo.email}`, {
                    autoClose: 3000,
                  });
                } else {
                  showToast.warning(`Email failed to send to ${emailInfo.role}: ${emailInfo.email}`, {
                    autoClose: 3000,
                  });
                }
              }, 800 * (index + 1));
            }
          });
        } else {
       
        }
        
        // Refresh date change requests immediately
        try {
          const requestsResponse = await AxiosInstance.get(
            `/v1/visit/date-change-requests/${location?.state?.id}/${location?.state?.contractId}`
          );
          
          if (requestsResponse?.data?.statusCode === 200) {
            setDateChangeRequests(requestsResponse?.data?.data || []);
            const pendingRequests = (requestsResponse?.data?.data || []).filter(req => req.Status === "Pending");
            if (pendingRequests.length > 0) {
              setPendingRequest(pendingRequests[0]);
            } else {
              setPendingRequest(null);
            }
          }
        } catch (error) {
          console.error("Error refreshing date change requests:", error);
        }
      }
    } catch (error) {
      console.error("Error creating date change request:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to submit the date change request. Please try again.";
      console.error("Error details:", {
        status: error?.response?.status,
        message: errorMessage,
        data: error?.response?.data,
      });
      
      // Show toast for all errors
      if (error?.response?.status === 400) {
        if (errorMessage.includes("already pending")) {
          showToast.warning("A visit date change request is already pending for this visit.");
        } else if (errorMessage.includes("already exists on")) {
          showToast.warning(errorMessage);
        } else {
          showToast.warning(errorMessage);
        }
      } else {
        showToast.error(errorMessage);
      }
    } finally {
      setDateChangeLoading(false);
    }
  };


  return (
    <Box sx={confirmLayout.page}>
      {loader ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "80vh" }}
        >
          <LoaderComponent loader={loader} height="50" width="50" />
        </Grid>
      ) : (
        <Fade in={!loader} timeout={800}>
          <Box sx={confirmLayout.contentWrap}>
                <Box mb={2}>
                  <BlueButton
                    className="customer-details-back-btn"
                    onClick={() => navigate(-1)}
                    aria-label="Go back"
                    label={
                      <ArrowBackOutlinedIcon
                        sx={{ fontSize: 22, color: "#ffffff" }}
                      />
                    }
                    style={{
                      marginRight: "10px",
                      width: "50px",
                      height: "40px",
                      minHeight: "40px",
                      minWidth: "50px",
                      maxWidth: "50px",
                      padding: "0",
                      borderRadius: "8px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  />
                </Box>

            <Box sx={confirmLayout.mainCard}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                  <Box sx={confirmLayout.headerBar}>
                    <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: "16px",
                          margin: 0,
                          lineHeight: 1.35,
                          color: "text.primary",
                        }}
                      >
                        {quotesData[0]?.VisitTitle || quotesData[0]?.ItemName || "Appointment Details"}
                      </Typography>
                      {quotesData[0]?.VisitTitle && (
                          <Typography
                          variant="body2"
                          sx={{
                            fontSize: "11px",
                            mt: "2px",
                            fontWeight: 400,
                            color: "text.secondary",
                          }}
                          >
                          Visit Title
                          </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                      }}
                    >
                          {pendingRequest && (
                        <Box
                              sx={{
                                backgroundColor: themeColors?.orange,
                                color: "white",
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "11px",
                                fontWeight: 600,
                            lineHeight: 1.2,
                              }}
                        >
                          Request Pending
                        </Box>
                      )}
                        {isConfirmed !== null && (
                          <>
                            {isConfirmed ? (
                              <Box
                                sx={{
                                  background: themeColors?.orange,
                                color: "white",
                                padding: "6px 14px",
                                borderRadius: "8px",
                                fontSize: "12px",
                                fontWeight: 600,
                                lineHeight: 1.2,
                                boxShadow: "var(--shadow-sm)",
                              }}
                                >
                                  Confirmed
                              </Box>
                            ) : (
                              moment(quotesData[0]?.StartDate).isSameOrAfter(
                                moment(),
                                "day"
                              ) && (
                                <Button
                                  disableElevation
                                  sx={{
                                    background: themeColors?.orange,
                                    color: "white",
                                  padding: "8px 16px",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                    textTransform: "none",
                                  borderRadius: "8px",
                                  minWidth: "auto",
                                  lineHeight: 1.2,
                                  boxShadow: "var(--shadow-md)",
                                  "&:hover": {
                                    background: themeColors?.orange,
                                    boxShadow: "var(--shadow-lg)",
                                  },
                                  }}
                                  onClick={handleConfirmAppointment}
                                >
                                  Confirm Appointment
                                </Button>
                              )
                            )}
                          </>
                        )}
                    </Box>
                  </Box>
                  <Box
                    className="bg-orange-color"
                    sx={{ height: "2px", width: "100%", flexShrink: 0 }}
                    aria-hidden
                  />
                  </Box>

                  {/* Content - same card design as staff appointment-confirm */}
                  <Box sx={confirmLayout.contentBody}>
                    <Grid container spacing={{ xs: 2, sm: 2.5 }} sx={{ mb: 3.5 }}>
                      {/* Contract Reference */}
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 6
                        }}>
                        <Box sx={confirmLayout.infoCard}>
                          <Typography sx={confirmLayout.infoLabel}>
                            Contract Reference
                          </Typography>
                          <Typography sx={{ color: "text.primary", fontSize: { xs: "14px", sm: "16px" }, fontWeight: "600", wordBreak: "break-word" }}>
                            {quotesData[0]?.ContractNumber || "Not Available"}
                            {(() => {
                              const contractTitle = quotesData[0]?.ContractTitle || quotesData[0]?.ContractData?.Title || quotesData[0]?.ContractData?.[0]?.Title;
                              return contractTitle ? ` | ${contractTitle}` : "";
                            })()}
                          </Typography>
                        </Box>
                      </Grid>
                      {/* Service Location */}
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 6
                        }}>
                        <Box sx={confirmLayout.infoCard}>
                          <Typography sx={confirmLayout.infoLabel}>
                            Service Location
                          </Typography>
                          <Typography sx={{ color: "text.primary", fontSize: { xs: "14px", sm: "16px" }, fontWeight: "600", wordBreak: "break-word" }}>
                            {quotesData[0]?.Location?.Address || "Address not available"}
                          </Typography>
                          <Typography sx={{ color: "text.secondary", fontSize: { xs: "12px", sm: "14px" }, mt: 0.5, wordBreak: "break-word" }}>
                            {quotesData[0]?.Location?.City || "City not available"}, {quotesData[0]?.Location?.State || "State not available"} {quotesData[0]?.Location?.Zip || "Zip not available"}
                          </Typography>
                        </Box>
                      </Grid>
                      {/* Customer */}
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 6
                        }}>
                        <Box sx={confirmLayout.infoCard}>
                          <Typography sx={confirmLayout.infoLabel}>
                            Customer
                          </Typography>
                          <Typography sx={{ color: "text.primary", fontSize: { xs: "14px", sm: "16px" }, fontWeight: "600", wordBreak: "break-word" }}>
                            {(() => {
                              const cp = quotesData[0]?.CustomerProfile;
                              if (cp) {
                                const name = [cp.FirstName, cp.LastName].filter(Boolean).join(" ").trim() || cp.OwnerName;
                                if (name) return name;
                              }
                              return "Customer";
                            })()}
                          </Typography>
                        </Box>
                      </Grid>
                      {/* Status Details */}
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 6
                        }}>
                        <Box sx={confirmLayout.infoCard}>
                          <Typography sx={confirmLayout.infoLabel}>
                            Status Details
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {quotesData[0]?.Status &&
                              String(quotesData[0].Status).trim() !== "" && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                  }}
                                >
                                  <JobberStatusBadge
                                    tone={getVisitAppointmentStatusTone(
                                      quotesData[0].Status
                                    )}
                                  >
                                    {quotesData[0].Status}
                                  </JobberStatusBadge>
                                </Box>
                              )}
                            {Array.isArray(quotesData[0]?.ConfirmWorkerDetails) && quotesData[0].ConfirmWorkerDetails.length > 0 && quotesData[0].ConfirmWorkerDetails.map((worker, index) => (
                              <Typography key={index} sx={{ color: "text.primary", fontWeight: 500, fontSize: { xs: "13px", sm: "14px" }, display: "flex", alignItems: "center", gap: "6px" }}>
                                <DoneIcon sx={{ fontSize: "14px", color: "green" }} />
                                {worker?.OwnerName ? `Confirmed By Account Owner - ${worker.OwnerName}` : worker?.FirstName && worker?.LastName ? `Confirmed By Worker - ${worker.FirstName} ${worker.LastName}` : "Confirmed By Worker - Unknown Worker"}
                              </Typography>
                            ))}
                            {quotesData[0]?.Status === "Completed" && Array.isArray(quotesData[0]?.ConfirmWorkerDetails) && quotesData[0].ConfirmWorkerDetails.length > 0 && quotesData[0].ConfirmWorkerDetails.map((worker, index) => (
                              <Typography key={`complete-${index}`} sx={{ color: "text.primary", fontWeight: 500, fontSize: { xs: "13px", sm: "14px" }, display: "flex", alignItems: "center", gap: "6px" }}>
                                <DoneIcon sx={{ fontSize: "14px", color: "green" }} />
                                {worker?.OwnerName ? `Completed By Account Owner - ${worker.OwnerName}` : worker?.FirstName && worker?.LastName ? `Completed By Worker - ${worker.FirstName} ${worker.LastName}` : "Completed By Worker - Unknown Worker"}
                              </Typography>
                            ))}
                            {quotesData[0]?.IsConfirm && (
                              <Typography sx={{ color: "text.primary", fontWeight: 500, fontSize: { xs: "13px", sm: "14px" }, display: "flex", alignItems: "center", gap: "6px" }}>
                                <DoneIcon sx={{ fontSize: "14px", color: "green" }} />
                                Confirmed By Customer - {quotesData[0]?.CustomerProfile ? `${quotesData[0].CustomerProfile.FirstName || ""} ${quotesData[0].CustomerProfile.LastName || ""}`.trim() : "Customer"}
                              </Typography>
                            )}
                            {!quotesData[0]?.IsConfirmByWorker &&
                              !quotesData[0]?.IsComplete &&
                              !quotesData[0]?.IsConfirm &&
                              (!Array.isArray(quotesData[0]?.ConfirmWorkerDetails) ||
                                quotesData[0].ConfirmWorkerDetails.length === 0) &&
                              (!quotesData[0]?.Status ||
                                String(quotesData[0].Status).trim() === "") && (
                                <JobberStatusBadge tone="neutral">
                                  No status updates available
                                </JobberStatusBadge>
                              )}
                          </Box>
                        </Box>
                      </Grid>
                      {/* Appointment Date & Time */}
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 6
                        }}>
                        <Box sx={confirmLayout.infoCard}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 1.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  ...confirmLayout.infoLabel,
                                  marginBottom: { xs: "4px", sm: "6px" },
                                }}
                              >
                                Appointment Date & Time
                              </Typography>
                              <Typography sx={{ color: "text.primary", fontSize: { xs: "14px", sm: "16px" }, fontWeight: "600", wordBreak: "break-word", display: "block", mb: 0.5 }}>
                                {formatDateWithUserTimezone(quotesData[0]?.StartDate, { dateFormat: DateDecode, includeSeconds: false, excludeTime: true })}
                              </Typography>
                              {(() => {
                                const startTime = formatTimeFromString(quotesData[0]?.StartTime);
                                const endTime = formatTimeFromString(quotesData[0]?.EndTime);
                                if (!startTime && !endTime) return null;
                                return (
                                  <Typography sx={{ color: "text.primary", fontSize: { xs: "13px", sm: "14px" }, fontWeight: 500, wordBreak: "break-word" }}>
                                    {startTime && <>Start Time: {startTime}</>}
                                    {startTime && endTime && " · "}
                                    {endTime && <>End Time: {endTime}</>}
                                  </Typography>
                                );
                              })()}
                            </Box>
                            {!isConfirmed && !isComplete && (
                              <Box
                                component="span"
                                onClick={handleOpenChangeDateDialog}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 36,
                                  height: 36,
                                  borderRadius: "8px",
                                  backgroundColor: ML_EDIT_TILE_BG,
                                  border: `1px solid ${ML_EDIT_TILE_BORDER}`,
                                  lineHeight: 0,
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  transition: "opacity 0.15s ease",
                                  "&:hover": { opacity: 0.92 },
                                }}
                              >
                                <EditIcon
                                  color={ML_EDIT_ICON}
                                  style={{
                                    marginRight: 0,
                                    fontSize: "16px",
                                    cursor: "pointer",
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                      {/* Assigned Workers */}
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 6
                        }}>
                        <Box sx={confirmLayout.infoCard}>
                          <Typography sx={confirmLayout.infoLabel}>
                            Assigned Workers
                          </Typography>
                          {(() => {
                            const workers = [];
                            const workerSet = new Set();
                            if (Array.isArray(quotesData[0]?.AssignPerson) && quotesData[0]?.AssignPerson.length > 0) {
                              quotesData[0].AssignPerson.forEach(person => {
                                const name = person?.FullName || (person?.FirstName && person?.LastName ? `${person.FirstName} ${person.LastName}` : null) || person?.OwnerName;
                                if (name && !workerSet.has(name)) { workers.push(name); workerSet.add(name); }
                              });
                            }
                            if (Array.isArray(quotesData[0]?.WorkerData) && quotesData[0]?.WorkerData.length > 0) {
                              quotesData[0].WorkerData.forEach(worker => {
                                const name = worker?.FirstName && worker?.LastName ? `${worker.FirstName} ${worker.LastName}` : worker?.FullName;
                                if (name && !workerSet.has(name)) { workers.push(name); workerSet.add(name); }
                              });
                            }
                            if (workers.length === 0) {
                              if (quotesData[0]?.AssignPerson?.FullName) workers.push(quotesData[0].AssignPerson.FullName);
                              else if (quotesData[0]?.AssignPerson?.FirstName && quotesData[0]?.AssignPerson?.LastName) workers.push(`${quotesData[0].AssignPerson.FirstName} ${quotesData[0].AssignPerson.LastName}`);
                              else if (quotesData[0]?.AssignPerson?.OwnerName) workers.push(quotesData[0].AssignPerson.OwnerName);
                              else if (quotesData[0]?.WorkerName) workers.push(quotesData[0].WorkerName);
                              else if (quotesData[0]?.AssignWorker) workers.push(quotesData[0].AssignWorker);
                            }
                            return workers.length > 0 ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                                {workers.map((worker, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      padding: "8px 14px",
                                      backgroundColor: "action.hover",
                                      borderRadius: "8px",
                                      border: 1,
                                      borderColor: "divider",
                                    }}
                                  >
                                    <Typography sx={{ color: "text.primary", fontWeight: 600, fontSize: "12px" }}>{worker}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography sx={{ color: "text.secondary", fontSize: "14px", fontStyle: "italic" }}>Not Assigned</Typography>
                            );
                          })()}
                        </Box>
                      </Grid>
                    </Grid>

                    <Grid container spacing={{ xs: 2.5, sm: 3 }}>
                      <Grid size={12}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 2.5 } }}>
                          {/* Materials & Labor - Always show */}
                          <Box>
                            <Box sx={{ marginBottom: { xs: 1.5, sm: 2 } }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "18px",
                                  color: "text.primary",
                                  margin: 0,
                                  paddingBottom: { xs: 1.25, sm: 1.5 },
                                }}
                              >
                                Materials & Labor
                              </Typography>
                              <Box
                                className="bg-orange-color"
                                sx={{ height: "2px", width: "100%", borderRadius: "2px" }}
                                aria-hidden
                              />
                            </Box>
                            <Box sx={confirmLayout.materialsPanel}>
                                {(() => {
                                  const items = quotesData[0]?.ContractItemData || 
                                                quotesData[0]?.ContractData?.[0]?.Items || 
                                                quotesData[0]?.Items || [];
                                  
                                  // Calculate totals
                                  let materialSubTotal = quotesData[0]?.MaterialSubTotal || 0;
                                  let laborSubTotal = quotesData[0]?.LaborSubTotal || 0;
                                  let totalTaxes = quotesData[0]?.TotalCalculatedTaxes || 0;
                                  let grandTotal = quotesData[0]?.Total || 0;
                                  
                                  // If totals not available, calculate from items
                                  if (materialSubTotal === 0 && laborSubTotal === 0 && items.length > 0) {
                                    items.forEach(item => {
                                      const total = parseFloat(item?.Total || 0);
                                      if (item?.Type === "Materials") {
                                        materialSubTotal += total;
                                      } else if (item?.Type === "Labor") {
                                        laborSubTotal += total;
                                      } else {
                                        materialSubTotal += total;
                                      }
                                    });
                                  }
                                  
                                  if (grandTotal === 0) {
                                    grandTotal = materialSubTotal + laborSubTotal + totalTaxes;
                                  }
                                  
                                  // Get tax information
                                  const contractData = quotesData[0]?.ContractData?.[0] || quotesData[0]?.ContractData || {};
                                  const taxPercent = parseFloat(contractData?.Tax || 0);
                                  const calculatedTaxes = quotesData[0]?.CalculatedTaxes || contractData?.CalculatedTaxes || [];
                                  
                                  // Calculate tax amount if percentage is provided but totalTaxes is 0
                                  if (taxPercent > 0 && totalTaxes === 0) {
                                    const subtotal = materialSubTotal + laborSubTotal;
                                    totalTaxes = (subtotal * taxPercent) / 100;
                                  }
                                  
                                  // Calculate total taxes from calculated taxes if not provided
                                  if (totalTaxes === 0 && calculatedTaxes.length > 0) {
                                    totalTaxes = calculatedTaxes.reduce((sum, tax) => sum + parseFloat(tax?.amount || 0), 0);
                                  }
                                  
                                  if (grandTotal === 0) {
                                    grandTotal = materialSubTotal + laborSubTotal + totalTaxes;
                                  }
                                  
                                  return items.length > 0 ? (
                                    <>
                                      <Box>
                                        {items.map((item, index) => (
                                          <Box
                                            key={item._id || item.ContractItemId || index}
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                              padding: "10px 0",
                                              borderBottom:
                                                index < items.length - 1 ? 1 : "none",
                                              borderColor: "divider",
                                            }}
                                          >
                                            <Typography
                                              sx={{
                                                fontSize: "13px",
                                                fontWeight: 500,
                                                color: "text.primary",
                                                flex: 1,
                                              }}
                                            >
                                              {item?.Name || "Materials and Labor not Available"}
                                            </Typography>
                                            <Typography
                                              sx={{
                                                textAlign: "right",
                                                fontSize: "13px",
                                                color: "text.primary",
                                                fontWeight: 500,
                                                minWidth: "120px",
                                              }}
                                            >
                                              {item.Unit
                                                ? `${item?.Unit} Units`
                                                : item.Hourly
                                                ? `${item?.Hourly} Hours`
                                                : item.Square
                                                ? `${item?.Square} SqFeet`
                                                : item.Fixed
                                                ? `${item?.Fixed} Fixed`
                                                : "Unit not available"}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                      
                                      {/* Totals Section */}
                                      <Box sx={{ mt: 2, pt: 2, borderTop: 2, borderColor: "divider" }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "6px" }}>
                                          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "12px", fontWeight: 400 }}>
                                            Material Subtotal:
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: "text.primary", fontSize: "12px", fontWeight: 500 }}>
                                            ${Number(materialSubTotal).toFixed(2)}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "6px" }}>
                                          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "12px", fontWeight: 400 }}>
                                            Labor Subtotal:
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: "text.primary", fontSize: "12px", fontWeight: 500 }}>
                                            ${Number(laborSubTotal).toFixed(2)}
                                          </Typography>
                                        </Box>
                                        {/* Show regular tax with percentage if exists */}
                                        {taxPercent > 0 && (
                                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "6px" }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "12px", fontWeight: 400 }}>
                                              Tax ({taxPercent}%):
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "text.primary", fontSize: "12px", fontWeight: 500 }}>
                                              ${Number(totalTaxes).toFixed(2)}
                                            </Typography>
                                          </Box>
                                        )}
                                        {/* Show calculated taxes with percentages */}
                                        {calculatedTaxes.length > 0 && calculatedTaxes.map((tax, taxIndex) => {
                                          const isPercentageTax = tax?.feeType === "Percentage" || (tax?.percentage && tax?.percentage > 0);
                                          const taxAmount = parseFloat(tax?.amount || 0);
                                          const taxPercentage = tax?.percentage || 0;
                                          const taxName = tax?.name || "Tax";
                                          const taxType = tax?.type ? ` - ${tax.type}` : "";
                                          
                                          if (taxAmount <= 0) return null;
                                          
                                          return (
                                            <Box key={taxIndex} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "6px" }}>
                                              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "12px", fontWeight: 400 }}>
                                                {isPercentageTax && taxPercentage > 0 
                                                  ? `${taxName} (${taxPercentage}%)${taxType}:`
                                                  : `${taxName}${taxType}:`}
                                              </Typography>
                                              <Typography variant="body2" sx={{ color: "text.primary", fontSize: "12px", fontWeight: 500 }}>
                                                ${Number(taxAmount).toFixed(2)}
                                              </Typography>
                                            </Box>
                                          );
                                        })}
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                                          <Typography variant="body1" sx={{ color: "text.primary", fontSize: "14px", fontWeight: 600 }}>
                                            Total:
                                          </Typography>
                                          <Typography variant="body1" sx={{ color: "text.primary", fontSize: "14px", fontWeight: 600 }}>
                                            ${Number(grandTotal).toFixed(2)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: "12px",
                                        textAlign: "center",
                                        padding: "16px",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      No materials and labor items available
                                    </Typography>
                                  );
                                })()}
                            </Box>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Date Change Requests - full width below, compact when empty */}
                      <Grid size={12}>
                        <Box sx={confirmLayout.dateChangeWrap}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              width: "100%",
                            }}
                          >
                            <Box sx={confirmLayout.dateChangeHeader}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "15px", margin: 0, color: "inherit" }}>
                                Date Change Requests
                              </Typography>
                            </Box>
                            <Box
                              className="bg-orange-color"
                              sx={{ height: "2px", width: "100%", flexShrink: 0 }}
                              aria-hidden
                            />
                          </Box>
                          <Box sx={{ padding: dateChangeRequests && dateChangeRequests.length > 0 ? { xs: "16px", sm: "20px" } : { xs: "14px 16px", sm: "16px 20px" }, maxHeight: dateChangeRequests && dateChangeRequests.length > 0 ? "400px" : "none", overflowY: dateChangeRequests && dateChangeRequests.length > 0 ? "auto" : "visible" }}>
                            {dateChangeRequests && dateChangeRequests.length > 0 ? (
                              dateChangeRequests.map((request, index) => (
                        <Box
                          key={request.RequestId || index}
                          sx={{
                                    padding: { xs: "14px", sm: "16px" },
                                    marginBottom: index < dateChangeRequests.length - 1 ? { xs: "12px", sm: "16px" } : 0,
                                    border: 1,
                                    borderColor: "divider",
                                    backgroundColor: "action.hover",
                                    borderRadius: "8px",
                          }}
                        >
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: "12px" }}>
                            <Typography
                                      variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                        fontSize: "13px",
                              }}
                            >
                                      {request.RequestedBy === "Customer" ? "You" : request.RequestedBy}
                            </Typography>
                                    <Box
                              sx={{
                                        backgroundColor: request.Status === "Pending" ? themeColors?.orange : request.Status === "Approved" ? "#28a745" : "#dc3545",
                                color: "white",
                                        padding: "4px 10px",
                                        borderRadius: "12px",
                                        fontSize: "11px",
                                fontWeight: 600,
                              }}
                                    >
                                      {request.Status}
                          </Box>
                                  </Box>
                                  <Box sx={{ mb: "6px" }}>
                                    <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "12px", display: "inline", fontWeight: 500 }}>
                                      Current:{" "}
                          </Typography>
                                    <Typography variant="body2" sx={{ color: "text.primary", fontSize: "12px", display: "inline", fontWeight: 600 }}>
                                      {formatDateWithUserTimezone(request.CurrentDate, {
                                        dateFormat: DateDecode,
                                        includeSeconds: false
                                      })}
                          </Typography>
                                  </Box>
                                  <Box sx={{ mb: "6px" }}>
                                    <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "12px", display: "inline", fontWeight: 500 }}>
                                      Requested:{" "}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "text.primary", fontSize: "12px", display: "inline", fontWeight: 600 }}>
                                      {formatDateWithUserTimezone(request.RequestedDate, {
                                        dateFormat: DateDecode,
                                        includeSeconds: false
                                      })}
                                    </Typography>
                                  </Box>
                          {request.Reason && (
                                    <Box sx={{ mb: "6px", mt: "8px", paddingTop: "8px", borderTop: 1, borderColor: "divider" }}>
                                      <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "12px", fontWeight: 500, mb: "4px" }}>
                                        Reason:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: "text.primary", fontSize: "12px", wordBreak: "break-word", overflowWrap: "break-word", lineHeight: 1.5, display: "block" }}>
                                        {request.Reason}
                                      </Typography>
                                    </Box>
                          )}
                          {request.Status !== "Pending" && request.ReviewReason && (
                                    <Box sx={{ mb: "6px", mt: "8px", paddingTop: "8px", borderTop: 1, borderColor: "divider" }}>
                                      <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "12px", fontWeight: 500, mb: "4px" }}>
                                        Admin Response:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: "text.primary", fontSize: "12px", wordBreak: "break-word", overflowWrap: "break-word", lineHeight: 1.5, display: "block", fontStyle: "italic" }}>
                                        {request.ReviewReason}
                                      </Typography>
                                    </Box>
                          )}
                        </Box>
                              ))
                            ) : (
                              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "13px", fontStyle: "italic", margin: 0 }}>
                                No date change requests available
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                  </Box>
                </Box>
          </Box>
        </Fade>
      )}
      <CustomSwal
        isOpen={showConfirmSwal}
        onClose={handleConfirmAppointmentCancel}
        onConfirm={handleConfirmAppointmentConfirm}
        title="Are you sure?"
        text="Do you want to confirm this appointment?"
        confirmButtonText="Yes, confirm it!"
        cancelButtonText="No, cancel"
        requireInput={false}
      />
      <CustomSwal
        isOpen={showSuccessSwal}
        onClose={() => setShowSuccessSwal(false)}
        onConfirm={() => setShowSuccessSwal(false)}
        title="Confirmed!"
        text="The appointment has been confirmed."
        confirmButtonText="OK"
        requireInput={false}
      />
      <CustomSwal
        isOpen={showErrorSwal}
        onClose={() => setShowErrorSwal(false)}
        onConfirm={() => setShowErrorSwal(false)}
        title="Unable to update appointment"
        text="We couldn't update your appointment. Please try again in a moment."
        confirmButtonText="OK"
        requireInput={false}
      />
      {/* Change Date Dialog */}
      <Dialog
        open={showChangeDateDialog}
        onClose={handleCloseChangeDateDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            overflow: "hidden",
            maxWidth: "500px",
            width: "100%",
            border: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            boxShadow: (t) =>
              t.palette.mode === "dark"
                ? "0 4px 24px rgba(0,0,0,0.45)"
                : "0 4px 24px rgba(15, 43, 70, 0.1)",
          },
        }}
      >
        <DialogTitle 
          sx={{ 
            backgroundColor: "background.paper",
            color: "text.primary",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              padding: "1.25rem 1.5rem 1rem",
            }}
          >
            <Typography
              component="span"
              variant="h6"
              sx={{
                fontSize: "1.125rem",
                fontWeight: 600,
                color: "text.primary",
                lineHeight: 1.3,
              }}
            >
              Change Appointment Date
            </Typography>
            <IconButton
              onClick={handleCloseChangeDateDialog}
              aria-label="Close"
              size="small"
              sx={{
                color: "text.secondary",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <CloseIcon fontSize="medium" />
            </IconButton>
          </Box>
          <Box
            className="bg-orange-color"
            sx={{ height: "2px", width: "100%", flexShrink: 0 }}
            aria-hidden
          />
        </DialogTitle>
        <DialogContent sx={{ padding: "1.5rem", pt: "1.25rem !important" }}>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: "text.secondary",
              fontSize: "0.875rem",
              lineHeight: 1.5,
            }}
          >
            Select a new date for your appointment
          </Typography>
          <Box sx={{ mb: 2.5 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="New Appointment Date"
                value={newDate}
                onChange={(newValue) => setNewDate(newValue)}
                format={DateDecode}
                minDate={dayjs()}
                shouldDisableDate={shouldDisableDate}
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.action.hover
                            : "#fff",
                        "& fieldset": {
                          borderColor: "divider",
                        },
                        "&:hover fieldset": {
                          borderColor: "text.disabled",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "text.secondary",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ width: "100%" }}>
            <InputText
              name="dateChangeReason"
              label="Reason for Date Change"
              placeholder="Please provide a reason for changing the appointment date"
              value={dateChangeReason}
              onChange={(e) => setDateChangeReason(e.target.value)}
              multiline
              rows={3}
              required
              fullWidth
              sx={{ width: "100%" }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.5rem", borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "space-between", gap: 1, bgcolor: "background.default" }}>
          <WhiteButton
            type="button"
            onClick={handleCloseChangeDateDialog}
            label="Cancel"
            style={{ minWidth: "100px" }}
          />
          <BlueButton
            onClick={handleChangeDateConfirm}
            disabled={dateChangeLoading || !newDate || !dateChangeReason?.trim()}
            label={dateChangeLoading ? "Submitting..." : "Submit Request"}
            style={{ minWidth: "120px" }}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Quotes;
